import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import { useStrictQuery } from "hooks/useStrictQuery";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  AgreementFilter,
  ListRequestOfAgreementFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import SelectAgreementStatuses from "./SelectAgreementStatuses";

const AgreementsList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    agreementService,
    agreementCreateUrl,
    agreementDetailsUrl,
  } = useGlobalContext();

  const searchTerm = persistanceStore.useState(
    (s) => s.agreementFilterSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const agreementStatusIds = persistanceStore.useState(
    (s) => s.agreementFilterStatusIds
  );

  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const { organizationId } = contextStore.useState();

  useListTitle("agreements");

  const { data, isFetching } = useStrictQuery(
    [
      "agreementService.getAgreements",
      organizationId,
      agreementStatusIds,
      debouncedSearchTerm,
      order,
      orderBy,
      page,
      rowsPerPage,
    ],
    () =>
      agreementService.getAgreements(
        ListRequestOfAgreementFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: AgreementFilter.fromJS({
            agreementStatusIds: agreementStatusIds.map((s) => s),
            searchText: debouncedSearchTerm,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "displayName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "agreementDisplayNameLabel" }),
    },
    {
      accessor: "agreementStatusName",
      customSortAccessor: "agreementStatus.Name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "statusLabel" }),
    },
    {
      accessor: "organizationName",
      customSortAccessor: "organization.Name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(agreementDetailsUrl(id));
  };

  return (
    <Container>
      <Box mb={3} justifyContent="center" display="flex">
        <Fab
          aria-label="add"
          color="primary"
          onClick={() => navigate(agreementCreateUrl())}
        >
          <AddIcon />
        </Fab>
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={8}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.agreementFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.agreementFilterSearchTerm = e.target.value;
                })
              }
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <SelectAgreementStatuses />
          </Grid>
        </Grid>
      </Box>

      <LazyTable
        {...{
          rows: data?.data ?? [],
          dense: true,
          title: t({ id: "agreements" }),
          cells,
          onClick,
          rowCount: data?.totalRecords ?? 0,
          loading: isFetching,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => agreementDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default AgreementsList;
